import CheckIcon from "@components/svg/checkIcon";
import React, { ReactElement, useState, useMemo } from "react";
import { getPhoneNumber, replacePlaceholders } from "@lib/sharedUtils";
import AffiliateOfferUrl from "@components/shared/affiliateOfferUrl";
import { AffiliateOfferZone, affiliateOfferZones } from "@lib/shared/config";
import { useDomainContext } from "@hooks/useDomainContext";
import styles from "./style.module.scss";
import StarIcon from "@components/svg/starIcon";
import BadgeIcon from "@components/svg/badgeIcon";
import { HiddenAttributesKeys, ListingItem } from "@lib/shared/types";
import Chevron from "@components/svg/chevron";
import GreenCheckCircle from "@components/svg/greenCheckCircle";
import BlueRightIcon from "@components/svg/blueRightIcon";
import OfferRating from "./OfferRating";
import OfferCTA from "./OfferCTA";

export default function Offer({
    offer,
    index,
    logoUrl,
    buttonText,
    title,
    bulletedDescription,
    revenue,
    slug,
    isExternal,
    featuredOfferMessage,
    featuredOfferMessagePosition = "cta",
    ratingPosition = "cta",
    zone,
    showIndex = false,
    showRating = false,
    showSummary = false,
    showSeparator = false,
    useHiddenAttributes = false,
    textUnderButton,
    hiddenAttributes = {},
    hideRatingText = false,
    starRating = false,
    onlyButtonAndLinksClickable = false,
    maxOffersWithAction,
    actionButtonText,
    hideDescription = false,
}: {
    offer?: ListingItem;
    index: number;
    logoUrl?: string;
    buttonText?: string;
    title?: string;
    bulletedDescription?: string[];
    revenue?: string | number;
    slug?: string;
    isExternal: boolean;
    featuredOfferMessage?: string;
    featuredOfferMessagePosition?: "cta" | "top" | "banner";
    ratingPosition?: "cta" | "middle" | "middleWithSeparator";
    showIndex?: boolean;
    showRating?: boolean;
    showSummary?: boolean;
    showSeparator?: boolean;
    zone?: string;
    useHiddenAttributes?: boolean;
    textUnderButton?: string;
    hiddenAttributes?: Partial<
        Record<HiddenAttributesKeys | "listItemTitle", string>
    >;
    hideRatingText?: boolean;
    starRating?: boolean;
    onlyButtonAndLinksClickable?: boolean;
    maxOffersWithAction?: number;
    actionButtonText?: string;
    hideDescription?: boolean;
}): ReactElement {
    const { isBot, visitDetails, category } = useDomainContext();
    const [clicked, setClicked] = useState(false);
    const [summaryExpanded, setSummaryExpanded] = useState(false);

    const {
        listItemTitle,
        listItemDescription,
        listItemFeatures,
        listItemTip1,
        listItemTip2,
        listItemExclusiveOffer,
        listItemReviewLink,
        listItemRating,
    } = hiddenAttributes;

    const featuredMessage = useMemo(() => {
        if (!featuredOfferMessage) return undefined;

        return replacePlaceholders(featuredOfferMessage, {
            category: category?.name,
            region: visitDetails?.region,
            city: visitDetails?.city,
        });
    }, [
        featuredOfferMessage,
        category?.name,
        visitDetails?.region,
        visitDetails?.city,
    ]);

    const featuresList = useMemo(() => {
        if (useHiddenAttributes && listItemFeatures?.length) {
            return listItemFeatures
                .replace(/^\[|\]$/g, "")
                .split(",")
                .map((item) => item.trim().replace(/^"|"$/g, ""));
        }

        return bulletedDescription;
    }, [useHiddenAttributes, listItemFeatures, bulletedDescription]);

    const rating = useMemo(() => {
        if (useHiddenAttributes && listItemRating) {
            return Number(listItemRating);
        }

        if (offer?.score) {
            return Math.min(Number(offer.score) * 2, 10);
        }

        return 5;
    }, [useHiddenAttributes, listItemRating, offer?.score]);

    const starRatingScore = useMemo(() => {
        if (useHiddenAttributes && listItemRating) {
            return Number(listItemRating) / 2;
        }

        return Number(offer?.score) || 5;
    }, [useHiddenAttributes, listItemRating, offer?.score]);

    if (isBot) {
        return <></>;
    }

    const isFeatured = index === 0 && Boolean(featuredMessage);

    const useActionBtn =
        Boolean(actionButtonText) && index < (maxOffersWithAction ?? 0);

    const phoneNumber = getPhoneNumber(offer);

    const ratingBlock = showRating ? (
        <>
            <div className={styles.mobileRatingVariant}>
                <OfferRating
                    rating={rating}
                    starRatingScore={starRatingScore}
                    starsCount={1}
                    starRating={true}
                    hideText={hideRatingText}
                />
            </div>
            <div className={styles.desktopRatingVariant}>
                <OfferRating
                    rating={rating}
                    starRatingScore={starRatingScore}
                    starRating={starRating}
                    hideText={hideRatingText}
                />
            </div>
        </>
    ) : (
        <></>
    );

    const handleClick = () => {
        setClicked(true);
    };

    return (
        <div
            className={`${styles["listing-block"]} ${
                (isFeatured && styles["featured"]) || ""
            } ${
                isFeatured && featuredOfferMessagePosition === "banner"
                    ? styles["listingBlockOverflow"]
                    : ""
            } ${index === 0 ? styles["firstOfferBlock"] : ""}`}
        >
            {/* Featured Banner Section */}
            {isFeatured && featuredOfferMessagePosition === "banner" ? (
                <div className={styles["bannerFeaturedOfferMessage"]}>
                    <span>{featuredMessage}</span>
                </div>
            ) : (
                <></>
            )}
            {((isFeatured && featuredOfferMessagePosition === "top") ||
                showIndex) && (
                <div className={styles["offer-index"]}>
                    {showIndex && (
                        <div className={styles.index}>
                            {String(index + 1).padStart(2, "0")}
                        </div>
                    )}
                    {isFeatured && featuredOfferMessagePosition === "top" ? (
                        <span
                            className={
                                styles["featured-offer-message-top"] +
                                ` ${!showIndex ? "ml-10 mt-2" : ""}`
                            }
                        >
                            {featuredMessage}
                        </span>
                    ) : (
                        <></>
                    )}
                </div>
            )}
            {isFeatured && !showIndex && (
                <div className={styles["order"]}>{<StarIcon />}</div>
            )}

            {useHiddenAttributes && listItemTip1 && (
                <div className={styles["mobileListItemTip1"]}>
                    <BadgeIcon />
                    <span> {listItemTip1}</span>
                </div>
            )}

            {/* Logo Section */}
            <div className={styles["logo"]}>
                <AffiliateOfferUrl
                    zone={
                        (zone as AffiliateOfferZone) ??
                        affiliateOfferZones.PAGE_BUILDER
                    }
                    target={!phoneNumber ? "_blank" : undefined}
                    offer={offer}
                    customHref={
                        phoneNumber ? `tel:${phoneNumber ?? ""}` : undefined
                    }
                >
                    <img src={logoUrl} alt="logo" loading="lazy" />
                </AffiliateOfferUrl>

                {ratingBlock}
            </div>

            {/* Content Section */}
            <div
                className={`${styles["content"]} ${
                    ratingPosition === "middle" ||
                    ratingPosition === "middleWithSeparator"
                        ? styles["contentWithRatingMiddle"]
                        : ""
                } ${showSeparator ? styles["contentShowSeparator"] : ""}`}
            >
                <div className={styles["offerHeaderTips"]}>
                    {useHiddenAttributes && listItemTip1 && (
                        <div className={styles["listItemTip1"]}>
                            <BadgeIcon />
                            <span> {listItemTip1}</span>
                        </div>
                    )}
                    {useHiddenAttributes && listItemTip2 && (
                        <div className={styles["listItemTip2"]}>
                            {listItemTip2}
                        </div>
                    )}
                </div>

                {useHiddenAttributes && listItemTitle ? (
                    <div className={styles["propsTitle"]}>{listItemTitle}</div>
                ) : (
                    <div
                        className={styles["title"]}
                        dangerouslySetInnerHTML={{ __html: title ?? "" }}
                    ></div>
                )}

                {useHiddenAttributes && listItemDescription && (
                    <div className={styles["propsDescription"]}>
                        {listItemDescription}
                    </div>
                )}

                <div className={styles["features"]}>
                    <ul>
                        {featuresList?.slice(0, 5)?.map((feature, idx) => (
                            <li key={idx}>
                                {useHiddenAttributes ? (
                                    <span className="children:h-4 children:w-4">
                                        <GreenCheckCircle />
                                    </span>
                                ) : (
                                    <span className="children:h-3 children:w-3">
                                        <CheckIcon />
                                    </span>
                                )}

                                <span
                                    className="ml-2"
                                    dangerouslySetInnerHTML={{
                                        __html: feature,
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                </div>

                {useHiddenAttributes && listItemReviewLink && useActionBtn && (
                    <a
                        href={`${listItemReviewLink}`}
                        target="_self"
                        rel="noreferrer"
                        className={styles["reviewLink"]}
                    >
                        Read Review <BlueRightIcon />
                    </a>
                )}
            </div>

            {/* Rating Section - middle */}
            {ratingPosition === "middle" ||
            ratingPosition === "middleWithSeparator" ? (
                <div
                    className={`${styles["ratingMiddle"]} ${
                        ratingPosition === "middleWithSeparator"
                            ? styles["middleWithSeparator"]
                            : ""
                    }`}
                >
                    {ratingBlock}
                </div>
            ) : (
                <></>
            )}

            {/* CTA Section */}
            <OfferCTA
                offer={offer}
                isFeatured={isFeatured}
                featuredOfferMessage={
                    featuredOfferMessagePosition == "cta"
                        ? featuredMessage
                        : undefined
                }
                clicked={clicked}
                onClick={handleClick}
                isExternal={isExternal}
                slug={slug}
                ratingBlock={ratingPosition === "cta" ? ratingBlock : <></>}
                revenue={revenue}
                phoneNumber={phoneNumber}
                buttonText={buttonText}
                actionButtonText={actionButtonText}
                useActionButton={useActionBtn}
                onlyButtonAndLinksClickable={onlyButtonAndLinksClickable}
                zone={zone}
                useHiddenAttributes={useHiddenAttributes}
                listItemExclusiveOffer={listItemExclusiveOffer}
                listItemReviewLink={listItemReviewLink}
                textUnderButton={textUnderButton}
                category={category?.name}
                city={visitDetails?.city}
                region={visitDetails?.region}
            />

            {/* Summary Section */}
            {showSummary && offer?.previewDescription && !hideDescription && (
                <div className={styles["summary-container"]}>
                    <div
                        onClick={() => {
                            setSummaryExpanded((prev) => !prev);
                        }}
                        className={styles["summary-toggle"]}
                    >
                        {summaryExpanded ? "Hide Summary" : "Show Summary"}
                        <Chevron
                            className={`${
                                summaryExpanded
                                    ? styles["chevron-up"]
                                    : styles["chevron-down"]
                            }`}
                        />
                    </div>
                    <div
                        className={`${styles["summary"]} ${
                            summaryExpanded ? styles["expanded"] : ""
                        }`}
                        dangerouslySetInnerHTML={{
                            __html: offer.previewDescription,
                        }}
                    ></div>
                </div>
            )}
        </div>
    );
}
